/**
 * 派单预约：左侧责任技师快捷标签（可选）+ 右侧选当班技师
 * - PC：TagSelectF + SelectF（antd）
 * - 手机嵌入等新订单 Mob：antd-mobile Selector（标签） + Picker 滚轮（当班技师）
 */
import React, { useMemo, useState, useCallback } from 'react'
import PropTypes from 'prop-types'
import { Picker, Selector } from 'antd-mobile'
import { DownOutlined, UserOutlined } from '@ant-design/icons'
import TagSelectF from '@/i2antdV5/TagSelectF'
import { SelectF } from '@/i2antdV5'

function bumpEmbeddedPickerPopupZ () {
  requestAnimationFrame(() => {
    const list = document.querySelectorAll('.adm-popup.adm-picker-popup')
    const el = list[list.length - 1]
    if (!el?.style) return
    el.style.zIndex = '11050'
    el.style.touchAction = 'manipulation'
  })
}

const MobileTechnicianColumnPicker = ({
  options,
  value,
  onChange,
  loading
}) => {
  const [visible, setVisible] = useState(false)

  const column = useMemo(
    () =>
      (options || []).map((o) => ({
        label: String(o.value ?? ''),
        value: String(o.id)
      })),
    [options]
  )

  const columns = useMemo(() => (column.length > 0 ? [column] : [[{ label: '暂无可选项', value: '__none__', disabled: true }]]), [column])

  const pickerValue = useMemo(() => {
    if (value == null || value === '') return undefined
    const s = String(value)
    if (column.length === 0) return undefined
    if (!column.some((c) => c.value === s)) return undefined
    return [s]
  }, [value, column])

  const selectedLabel =
    pickerValue?.length &&
    column.find((c) => c.value === pickerValue[0])?.label

  const handleConfirm = useCallback(
    (val) => {
      if (!val?.length || val[0] === '__none__') {
        setVisible(false)
        return
      }
      onChange(val[0])
      setVisible(false)
    },
    [onChange]
  )

  const handleAfterShow = useCallback(() => {
    bumpEmbeddedPickerPopupZ()
  }, [])

  return (
    <>
      <button
        type="button"
        className="project-plan-technician-picker__mob-trigger"
        disabled={loading || column.length === 0}
        onClick={() => setVisible(true)}
      >
        <span className="project-plan-technician-picker__mob-trigger-text">
          {selectedLabel != null && selectedLabel !== ''
            ? (
                selectedLabel
              )
            : (
            <span className="project-plan-technician-picker__mob-placeholder">请选择技师</span>
              )}
        </span>
        <span className="project-plan-technician-picker__mob-trigger-ic" aria-hidden>
          <UserOutlined />
          <DownOutlined className="project-plan-technician-picker__mob-caret" />
        </span>
      </button>
      <Picker
        visible={visible}
        onClose={() => setVisible(false)}
        columns={columns}
        value={pickerValue}
        title="选择当班技师"
        cancelText="取消"
        confirmText="确定"
        closeOnMaskClick
        getContainer={() => document.body}
        afterShow={handleAfterShow}
        loading={!!loading}
        onConfirm={handleConfirm}
        mouseWheel
      />
    </>
  )
}

const ProjectPlanTechnicianPicker = ({
  value,
  onChange,
  options,
  tagOptions,
  loading,
  storeScopeKey,
  useMobilePicker
}) => {
  const storeScopeKeyRef = React.useRef()

  React.useEffect(() => {
    if (storeScopeKey === undefined) return
    if (storeScopeKeyRef.current === undefined) {
      storeScopeKeyRef.current = storeScopeKey
      return
    }
    if (String(storeScopeKeyRef.current ?? '') === String(storeScopeKey ?? '')) return
    storeScopeKeyRef.current = storeScopeKey
  }, [storeScopeKey])

  const hasTagOptions = Array.isArray(tagOptions) && tagOptions.length > 0

  const selectorTagOptions = useMemo(
    () =>
      (tagOptions || []).map((o) => ({
        label: o.value ?? o.label,
        value: String(o.id)
      })),
    [tagOptions]
  )

  const selectorVal = value != null && value !== ''
    ? [String(value)]
    : []

  const mobileOuterClass =
    useMobilePicker
      ? 'project-plan-technician-picker project-plan-technician-picker--mobile-adm'
      : 'project-plan-technician-picker'

  if (useMobilePicker) {
    return (
      <div
        className={mobileOuterClass}
        style={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: 12, width: '100%' }}
      >
        {hasTagOptions
          ? (
          <div className="project-plan-technician-picker__tags-adm">
            <div className="form-hint form-hint-muted project-plan-technician-picker__hint">责任技师</div>
            <Selector
              columns={2}
              options={selectorTagOptions}
              multiple={false}
              showCheckMark
              value={selectorVal}
              onChange={(next) => {
                const picked = Array.isArray(next) && next.length > 0 ? next[0] : undefined
                onChange(picked)
              }}
            />
          </div>
            )
          : null}
        <div className="project-plan-technician-picker__staff-adm" style={{ width: '100%' }}>
          {!hasTagOptions
            ? (
              <div className="form-hint form-hint-muted project-plan-technician-picker__hint">本店当班技师</div>
              )
            : (
              <div className="form-hint form-hint-muted project-plan-technician-picker__hint">或选择当班技师</div>
              )}
          <MobileTechnicianColumnPicker
            options={options}
            value={value}
            onChange={onChange}
            loading={loading}
          />
        </div>
      </div>
    )
  }

  return (
    <div
      className={mobileOuterClass}
      style={{ display: 'flex', alignItems: 'flex-start', gap: 12, width: '100%' }}
    >
      {hasTagOptions
        ? (
        <div className="project-plan-technician-picker__tags" style={{ flex: '0 1 50%', minWidth: 0 }}>
          <div className="form-hint form-hint-muted" style={{ marginBottom: 6, fontSize: 12 }}>责任技师</div>
          <TagSelectF
            options={tagOptions}
            radio
            value={value != null && value !== '' ? [String(value)] : []}
            onChange={(v) => {
              const next = Array.isArray(v) && v.length > 0 ? v[0] : undefined
              onChange(next)
            }}
          />
        </div>
          )
        : null}
      <div
        className="project-plan-technician-picker__select"
        style={{ flex: '1 1 50%', minWidth: 140, maxWidth: '100%' }}
      >
        {!hasTagOptions
          ? (
          <div className="form-hint form-hint-muted" style={{ marginBottom: 6, fontSize: 12 }}>本店当班技师</div>
            )
          : (
          <div className="form-hint form-hint-muted" style={{ marginBottom: 6, fontSize: 12 }}>或选择当班技师</div>
            )}
        <SelectF
          options={options || []}
          value={value}
          onChange={(id) => onChange(id)}
          modalProps={{
            placeholder: '请选择技师',
            allowClear: true,
            showSearch: true,
            loading: !!loading,
            style: { width: '100%', minWidth: 0 }
          }}
        />
      </div>
    </div>
  )
}

ProjectPlanTechnicianPicker.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func,
  options: PropTypes.array,
  tagOptions: PropTypes.array,
  loading: PropTypes.bool,
  storeScopeKey: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /** true：antd-mobile Selector + Picker（与 newOrdersMob 日历同场景） */
  useMobilePicker: PropTypes.bool
}

export default ProjectPlanTechnicianPicker
